import { useState } from "react";
import { NAV, CONTACT_EMAIL, CONTACT_PHONE } from "../../data";
import { subscribe } from "../../lib/api";
import { toast } from "sonner";
import { scrollToId } from "./primitives";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import Logo from "./Logo";
import { Linkedin, Instagram, Twitter, Facebook, ArrowRight } from "lucide-react";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const join = async (e) => {
    e.preventDefault();
    if (!email) { toast.error("Enter your email."); return; }
    setLoading(true);
    try {
      await subscribe(email);
      toast.success("You're in. Growth insights incoming.");
      setEmail("");
    } catch (err) {
      toast.error(err?.response?.status === 409 ? "You're already subscribed." : "Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <footer className="relative border-t border-border/10 noise-bg">
      <div className="mx-auto max-w-7xl px-6 py-20">
        <div className="rounded-[2rem] bg-brand-gradient p-8 md:p-14 mb-16">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="font-display text-3xl md:text-4xl font-extrabold text-white tracking-tight">Grow with the VYFT letter.</h3>
              <p className="mt-3 text-white/85 max-w-md">Monthly playbooks on events, branding, performance marketing and AI. No fluff.</p>
            </div>
            <form data-testid="newsletter-form" onSubmit={join} className="flex gap-3">
              <Input data-testid="newsletter-email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="you@company.com" className="rounded-full h-13 py-6 bg-white/20 border-white/30 text-white placeholder:text-white/60" />
              <Button data-testid="newsletter-submit" type="submit" disabled={loading} className="rounded-full h-13 py-6 px-6 bg-white text-primary hover:bg-white/90 shrink-0">
                {loading ? "..." : <ArrowRight className="h-5 w-5" />}
              </Button>
            </form>
          </div>
        </div>

        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <Logo showTag />
            <p className="mt-5 text-muted-foreground max-w-xs">Ideas. Strategy. Growth. Your 360° business growth partner across events, media, branding and technology.</p>
            <div className="mt-6 flex gap-3">
              {[Linkedin, Instagram, Twitter, Facebook].map((Icon, i) => (
                <a key={i} href="#top" onClick={(e) => { e.preventDefault(); scrollToId("#top"); }} className="h-10 w-10 grid place-items-center rounded-full border border-border/15 hover:bg-brand-gradient hover:text-white hover:border-transparent transition-colors">
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>
          <div className="md:col-span-2">
            <h4 className="font-semibold mb-4">Navigate</h4>
            <ul className="space-y-3 text-sm text-muted-foreground">
              {NAV.map((n) => <li key={n.href}><button onClick={() => scrollToId(n.href)} className="hover:text-primary transition-colors">{n.label}</button></li>)}
              <li><a href="/careers" className="hover:text-primary transition-colors">Careers</a></li>
            </ul>
          </div>
          <div className="md:col-span-3">
            <h4 className="font-semibold mb-4">Services</h4>
            <ul className="space-y-3 text-sm text-muted-foreground">
              {["Event Management", "Digital Marketing", "Branding", "Media Production", "Technology"].map((s) => <li key={s}><button onClick={() => scrollToId("#services")} className="hover:text-primary transition-colors">{s}</button></li>)}
            </ul>
          </div>
          <div className="md:col-span-3">
            <h4 className="font-semibold mb-4">Get in touch</h4>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li>{CONTACT_EMAIL}</li>
              <li>{CONTACT_PHONE}</li>
              <li>Available in 18 cities</li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-border/10 flex flex-col md:flex-row justify-between gap-4 text-sm text-muted-foreground">
          <span>© {new Date().getFullYear()} VYFT Agency. All rights reserved.</span>
          <div className="flex gap-6">
            <button className="hover:text-primary transition-colors">Privacy Policy</button>
            <button className="hover:text-primary transition-colors">Terms</button>
          </div>
        </div>
      </div>
    </footer>
  );
}
